import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { cloneDeep } from 'lodash-es';
import { Subject, firstValueFrom, takeUntil } from 'rxjs';
import { AlertService } from '../../../../../../../core/services/alert/alert.service';
import { ApiService } from '../../../../../../../core/services/api/api.service';
import { ToastService } from '../../../../../../../core/services/toast/toast.service';
import { EnfafInputComponent } from '../../../../../../../components/enfaf-input/enfaf-input.component';
import { EnfafModalComponent } from '../../../../../../../components/enfaf-modal/enfaf-modal.component';
import { DomSanitizer } from '@angular/platform-browser';
import { CourseModality } from '../../../../../../../models/enums/course-mode.enum';
import { CourseFieldError } from '../../../../../../../models/interfaces/course-edition.interface';
import {
  MASTER_NAME_INPUT_MAXLENGTH,
  MASTER_NAME_MAX_LENGTH,
  buildEditionName,
  codePointLength,
  stripEditionSuffix
} from '../../../modal-edit-course/course-edition/course-edition.utils';

/** Data the dialog is opened with (`ModalService.openCloneCourseModal`). */
export interface CloneCourseDialogData {
  courseId: string;
  courseName: string;
  modality?: CourseModality | null;
  /** Master name of a root; an edition has none and its name without the suffix is used instead. */
  masterName?: string | null;
}

/**
 * Master name of the clone of a Máster: what was typed without a trailing « - nª Edición» (the server strips it too).
 * Required and up to 86 characters (code points), like the server (`course.masterNameRequired` / `masterNameTooLong`).
 */
const cloneMasterNameValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const masterName = stripEditionSuffix(typeof control.value === 'string' ? control.value.trim() : null);
  if (!masterName) return { required: true };
  const actualLength = codePointLength(masterName);
  return actualLength > MASTER_NAME_MAX_LENGTH ? { maxlength: { requiredLength: MASTER_NAME_MAX_LENGTH, actualLength } } : null;
};

@Component({
  selector: 'app-modal-clone-course',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    EnfafInputComponent,
    EnfafModalComponent,
    TranslatePipe
  ],
  templateUrl: './modal-clone-course.component.html',
  styleUrl: './modal-clone-course.component.scss'
})
export class ModalCloneCourseComponent implements OnInit, OnDestroy {
  courseId!: string;
  courseName!: string;
  /**
   * The source is a Máster (root or edition): its clone is a new, independent Máster (ENF-2372, E-9). The field is
   * then its master name and the server builds «{masterName} - 1ª Edición».
   */
  isMaster = false;
  readonly masterNameMaxLengthAttr = MASTER_NAME_INPUT_MAXLENGTH;
  showCloseAlert = false;
  courseForm: FormGroup = new FormGroup({});
  isSavingCourse = false;

  private unsubscribe$ = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: CloneCourseDialogData | null,
    private dialogRef: MatDialogRef<ModalCloneCourseComponent>,
    private alert: AlertService,
    private api: ApiService,
    private toast: ToastService,
    private translate: TranslateService,
    private iconRegistry: MatIconRegistry,
    private sanitizer: DomSanitizer
  ) {
    this.iconRegistry.addSvgIcon('copy', this.sanitizer.bypassSecurityTrustResourceUrl('assets/icon/copy.svg'));
  }

  get nameFC(): FormControl<string | null> {
    return this.courseForm.get('name') as FormControl<string | null>;
  }

  /** Master name that will be sent: the typed text trimmed and without an edition suffix. */
  get cloneMasterName(): string | null {
    return stripEditionSuffix(this.nameFC?.value?.trim());
  }

  /** «Nombre de la 1ª edición: «{masterName} - 1ª Edición».» under the field of a Máster. */
  get masterNameHint(): string {
    const masterName = this.cloneMasterName;
    return this.translate.instant('COURSE.EDITION.MASTER_NAME_HINT', { name: masterName ? buildEditionName(masterName, 1) : '—' });
  }

  get masterNameErrorLabel(): string {
    const control = this.nameFC;
    if (!control) return '';
    if (control.hasError('serverError')) return String(control.getError('serverError'));
    if (control.hasError('maxlength')) return this.translate.instant('COURSE.EDITION.ERRORS.NAME_TOO_LONG', { max: MASTER_NAME_MAX_LENGTH });
    if (control.hasError('required')) return this.translate.instant('COURSE.EDITION.ERRORS.MASTER_NAME_REQUIRED');
    return '';
  }

  ngOnInit(): void {
    this.initForm();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  async confirmCloseModal(): Promise<any> {
    if (this.showCloseAlert) {
      const closeModal = await this.alert.presentCloseWindowAlert();
      return closeModal ? this.closeModal() : null;
    }

    this.closeModal();
  }

  closeModal(result?: any): void {
    this.dialogRef.close(result);
  }

  initForm(): void {
    const receivedData = this.data ? cloneDeep(this.data) : null;
    this.courseId = receivedData?.courseId as string;
    this.courseName = receivedData?.courseName as string;
    if (!this.courseId || !this.courseName) {
      this.closeModal();
      return;
    }

    this.isMaster = receivedData?.modality === CourseModality.MASTER;
    if (this.isMaster) {
      // Prefilled with the master name of the source (a root sends it; an edition, its name without the suffix).
      const masterName = receivedData?.masterName?.trim() || stripEditionSuffix(this.courseName);
      this.courseForm = new FormGroup({
        name: new FormControl<string | null>(masterName, [cloneMasterNameValidator])
      });
      // A prefilled name that is already too long shows its error from the start.
      if (this.nameFC.invalid) this.nameFC.markAsTouched();
      return;
    }

    this.courseForm = new FormGroup({
      name: new FormControl(null, Validators.required)
    });
  }

  listenForChanges(): void {
    this.courseForm.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
      this.showCloseAlert = true;
    });
  }

  async cloneCourse(): Promise<void> {
    if (this.isSavingCourse) return;
    if (this.isMaster && this.nameFC.invalid) {
      this.nameFC.markAsTouched();
      return;
    }
    try {
      this.isSavingCourse = true;
      const body = {
        courseId: this.courseId,
        // A Máster sends its master name (without a typed edition suffix): the server names the clone
        // «{masterName} - 1ª Edición».
        name: this.isMaster ? this.cloneMasterName : this.courseForm.value.name
      };
      const response = await firstValueFrom(this.api.cloneCourse(body));
      this.isSavingCourse = false;

      // The name the server gave the clone (for a Máster, «{masterName} - 1ª Edición»), not the typed text.
      const clonedName = response?.courseUpdated?.name || body.name;
      this.toast.presentSuccessToast(this.translate.instant('COURSE.LIST.TOASTS.CLONED', { source: this.courseName, name: clonedName }));
      this.closeModal({ courseUpdated: response.courseUpdated });
    } catch (errorResponse: any) {
      this.isSavingCourse = false;
      const serverError: CourseFieldError = errorResponse?.error ?? {};
      const errorMessage = serverError.message;
      // A name error of a Máster (`course.masterNameRequired` / `masterNameTooLong`) goes to the field itself.
      if (this.isMaster && (serverError.field === 'masterName' || serverError.field === 'name')) {
        this.nameFC.setErrors({ serverError: errorMessage || this.translate.instant('COURSE.EDITION.ERRORS.FIELD_INVALID') });
        this.nameFC.markAsTouched();
        return;
      }
      this.toast.presentErrorToast(errorMessage || 'Hubo un error clonando curso');
    }
  }
}
